import type { Filter } from 'react-table'
import type { BulkActionResponse } from '#/api/models/bulkActionResponse'
import {
  getRowName,
  getSurveyFlatPaths,
  getVirtualSupplementalFieldsForBulkActions,
  injectSupplementalRowsIntoListOfRows,
} from '#/assetUtils'
import { getSupplementalPathParts } from '#/components/processing/processingUtils'
import {
  DROPDOWN_FILTER_QUESTION_TYPES,
  EXCLUDED_COLUMNS,
  EXCLUDED_ROW_TYPES,
  FILTER_EXACT_TYPES,
  LAST_COLUMNS_ORDER,
  SUBMISSION_ACTIONS_ID,
  TEXT_FILTER_QUESTION_IDS,
  TEXT_FILTER_QUESTION_TYPES,
  VALIDATION_STATUS_ID_PROP,
} from '#/components/submissions/tableConstants'
import { ValidationStatusAdditionalName } from '#/components/submissions/validationStatus.constants'
import {
  GROUP_TYPES_BEGIN,
  GROUP_TYPES_END,
  META_QUESTION_TYPES,
  QUESTION_TYPES,
  SUPPLEMENTAL_DETAILS_PROP,
} from '#/constants'
import type { AnyRowTypeName } from '#/constants'
import type { AssetResponse, SubmissionResponse, SurveyChoice, SurveyRow } from '#/dataInterface'
import { recordKeys, recordValues } from '#/utils'
import type { TableColumn } from './table.types'

const ATTACHMENT_QUESTION_TYPES = new Set<AnyRowTypeName>([
  QUESTION_TYPES.audio.id,
  QUESTION_TYPES['background-audio'].id,
  QUESTION_TYPES.image.id,
  QUESTION_TYPES.video.id,
  QUESTION_TYPES.file.id,
])

/**
 * Builds a lookup from a Data Table column key to the survey row its cells take the question type,
 * the choice labels and the media rendering from. Whole paths first; a column left from before its
 * question moved falls back to a single namesake question.
 */
export function buildColumnRowFinder(survey: SurveyRow[]) {
  // A fallback for rows too old to carry `$xpath`: keyed by name, so namesakes collide here.
  const flatPaths = getSurveyFlatPaths(survey, true, true)
  const rowsByPath = new Map<string, SurveyRow>()
  // `undefined` marks a name several questions share, i.e. one to give up on.
  const rowsByUniqueName = new Map<string, SurveyRow | undefined>()

  for (const row of survey) {
    const rowName = getRowName(row)
    const rowPath = row.$xpath ?? flatPaths[rowName]
    if (rowPath) {
      rowsByPath.set(rowPath, row)
    }
    rowsByUniqueName.set(rowName, rowsByUniqueName.has(rowName) ? undefined : row)
  }

  return (columnKey: string): SurveyRow | undefined => {
    const exactMatch = rowsByPath.get(columnKey)
    if (exactMatch) {
      return exactMatch
    }

    // No question sits at a supplemental path, so its leaf must not match one by name.
    if (getSupplementalPathParts(columnKey).type !== null) {
      return undefined
    }

    return rowsByUniqueName.get(columnKey.split('/').at(-1) ?? columnKey)
  }
}

/**
 * Builds a human-readable Data Table column label
 */
export function getColumnLabel(
  asset: AssetResponse,
  key: string,
  showGroupName: boolean,
  translationIndex = 0,
): string {
  // NOTE: Some very old code has something to do with nonexistent/negative
  // translationIndex. No idea what is that. It does influences returned value.
  const showLabels = translationIndex > -1

  if (asset.content?.survey === undefined) {
    return key
  }

  let question
  let questionPath: string[] = []
  if (key.includes('/')) {
    questionPath = key.split('/')
    question = asset.content.survey.find(
      (o) => o.name === questionPath[questionPath.length - 1] || o.$autoname === questionPath[questionPath.length - 1],
    )
  } else {
    questionPath = [key]
    question = asset.content.survey.find((o) => o.name === key || o.$autoname === key)
  }

  // This identifies the supplemental details column. For such column question
  // was not found by the `key` provided (because it starts with
  // `SUPPLEMENTAL_DETAILS_PROP`) - we will find the question based on the value
  // returned by `getSupplementalPathParts` later.
  if (question === undefined && questionPath[0] === SUPPLEMENTAL_DETAILS_PROP) {
    const supplementalPathParts = getSupplementalPathParts(key)

    const sourceName = supplementalPathParts.sourceRowPath

    // Supplemental details keys are built like one of:
    // - prefix / source question name / transcript _ language code
    // - prefix / source question name / translated _ language code
    // e.g. `_supplementalDetails/Wie_heisst_du/transcript_de`
    const sourceQuestionLabel = getColumnLabel(asset, sourceName, showGroupName, translationIndex)

    if (supplementalPathParts.type === 'transcript') {
      return `${t('transcript')} (${supplementalPathParts.languageCode}) | ${sourceQuestionLabel}`
    } else if (supplementalPathParts.type === 'translation') {
      return `${t('translation')} (${supplementalPathParts.languageCode}) | ${sourceQuestionLabel}`
    } else {
      // this is absurd, to undo what `injectSupplementalRowsIntoListOfRows()`
      // did when the back end already provided what's needed in the first
      // place
      const dtpath = key.slice('_supplementalDetails/'.length)
      // FIXME: pass the entire object (or at least the label!) provided by
      // the back end through to this function, without doing all this nonsense
      const analysisQuestion = asset.analysis_form_json?.additional_fields.find((f) => f.dtpath === dtpath)

      // Here we want to produce "Verified | Analysis question | Source question" type of label
      if (analysisQuestion?.type === 'qualVerification') {
        const parentQuestion = asset.analysis_form_json?.additional_fields.find(
          (f) => f.dtpath === analysisQuestion.source,
        )
        if (parentQuestion?.label) {
          return `${t('Verified')} | ${parentQuestion.label} | ${sourceQuestionLabel}`
        }
        return `${t('Verified')} | ${sourceQuestionLabel}`
      }

      if (analysisQuestion?.label) {
        return `${analysisQuestion.label} | ${sourceQuestionLabel}`
      }
    }
  }

  // Background audio questions don't have labels, but we need something to be
  // displayed to users.
  if (key === QUESTION_TYPES['background-audio'].id && showLabels) {
    return t('Background audio')
  }

  if (key === SUBMISSION_ACTIONS_ID) {
    return t('Multi-select checkboxes column')
  }
  if (key === VALIDATION_STATUS_ID_PROP) {
    return t('Validation')
  }

  let label = key

  if (key.includes('/')) {
    const splitK = key.split('/')
    label = splitK[splitK.length - 1]
  }
  if (question?.label && showLabels && question.label[translationIndex]) {
    label = question.label[translationIndex]
  }
  // show Groups in labels, when selected
  if (showGroupName && questionPath && key.includes('/')) {
    let gLabels = questionPath.join(' / ')

    if (showLabels) {
      const gT = questionPath.map((g) => {
        const x = asset.content?.survey?.find((o) => o.name === g || o.$autoname === g)
        if (x?.label && x.label[translationIndex]) {
          return x.label[translationIndex]
        }

        return g
      })
      gLabels = gT.join(' / ')
    }
    return gLabels
  }

  return label
}

/**
 * Returns concatenated HXL tags for a column, or null when none exist
 */
export function getColumnHXLTags(survey: SurveyRow[], key: string) {
  const colQuestion: SurveyRow | undefined = survey.find((question) => question.$autoname === key)
  if (!colQuestion || !colQuestion.tags) {
    return null
  }
  const HXLTags: string[] = []
  colQuestion.tags.forEach((tag) => {
    if (tag.startsWith('hxl:')) {
      HXLTags.push(tag.replace('hxl:', ''))
    }
  })
  if (HXLTags.length === 0) {
    return null
  } else {
    return HXLTags.join('')
  }
}

/**
 * Finds the field name used for background audio, if the form has one
 */
export function getBackgroundAudioQuestionName(asset: AssetResponse): string | null {
  return asset?.content?.survey?.find((item) => item.type === QUESTION_TYPES['background-audio'].id)?.name || null
}

/**
 * Selects a value for a Data Table column key from submission data.
 *
 * For repeat/group data, some submissions store answers under a parent path
 * (e.g. `group_a/group_b`) rather than the full question key
 * (`group_a/group_b/question`). This helper returns the closest matching
 * container payload in such cases.
 */
export function selectNestedRow(row: SubmissionResponse, key: string, rootParentGroup: string | undefined) {
  // Supplemental details should always use exact keys.
  if (key.startsWith(SUPPLEMENTAL_DETAILS_PROP)) {
    return row[key]
  }

  // Prefer exact key lookup whenever available.
  if (Object.prototype.hasOwnProperty.call(row, key)) {
    return row[key]
  }

  // If exact key is missing, find the closest existing parent path.
  // Example: key `group_a/group_b/question` with data stored at `group_a/group_b`.
  const keyPathSegments = key.split('/')
  for (let i = keyPathSegments.length - 1; i >= 1; i--) {
    const parentPath = keyPathSegments.slice(0, i).join('/')
    if (Object.prototype.hasOwnProperty.call(row, parentPath)) {
      const parentValue = row[parentPath]

      // Parent fallback is only valid for container-like values (repeat/group payloads).
      // This avoids accidentally targeting an unrelated scalar from a shorter matching path.
      if (Array.isArray(parentValue) || (typeof parentValue === 'object' && parentValue !== null)) {
        return parentValue
      }
    }
  }

  // Backward-compatible fallback for root-level repeat groups.
  if (rootParentGroup && Object.prototype.hasOwnProperty.call(row, rootParentGroup)) {
    const rootParentValue = row[rootParentGroup]
    if (Array.isArray(rootParentValue) || (typeof rootParentValue === 'object' && rootParentValue !== null)) {
      return rootParentValue
    }
  }

  return row[key]
}

/**
 * Checks whether a value is meaningfully present in submission data
 */
function hasNonEmptyValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== ''
}

/**
 * Checks which of the two paths this submission has an attachment filed under.
 *
 * Only `question_xpath` counts, as the back end recorded it. File names are no evidence:
 * two questions can hold files of the same name, and a stored name need not match the
 * response that named it.
 */
function getAttachmentPathEvidence(
  submission: SubmissionResponse,
  legacyKey: string,
  currentPath: string,
): 'none' | 'legacy-only' | 'both' {
  const matchingAttachments = (submission._attachments || []).filter(
    (attachment) =>
      !attachment.is_deleted && (attachment.question_xpath === legacyKey || attachment.question_xpath === currentPath),
  )

  const hasLegacyPathAttachment = matchingAttachments.some((attachment) => attachment.question_xpath === legacyKey)
  const hasCurrentPathAttachment = matchingAttachments.some((attachment) => attachment.question_xpath === currentPath)

  // A file on each path means two files, however alike the responses naming them look.
  if (hasLegacyPathAttachment && hasCurrentPathAttachment) {
    return 'both'
  }

  // A file on the current path alone belongs to the current question and says nothing
  // about the legacy response, which may well name a different file of the same name.
  if (hasLegacyPathAttachment) {
    return 'legacy-only'
  }

  return 'none'
}

/**
 * Decides if a legacy attachment column is safe to drop.
 *
 * We only collapse stale/current columns when metadata strongly suggests they
 * are the same field. Any conflict (different values or evidence on both
 * paths) keeps the legacy column.
 *
 * The one file has to be filed under the legacy path, as that is the file the surviving
 * column then renders in its place. Anything weaker risks dropping a column whose
 * response names a file of its own.
 */
export function shouldDropLegacyAttachmentColumn(
  submissions: SubmissionResponse[],
  legacyKey: string,
  matchingCurrentPaths: string[],
): boolean {
  // Only collapse legacy path when there is strong evidence from attachment
  // metadata that old/new keys mirror the same field.
  // Keep the legacy key if values differ, or if attachment metadata indicates
  // both paths are genuinely present.
  let hasMirroredAttachmentEvidence = false

  for (const submission of submissions) {
    const legacyValue = submission[legacyKey]
    if (!hasNonEmptyValue(legacyValue)) {
      continue
    }

    // If this submission only has legacy data and no current-path value,
    // dropping legacy would hide data from pre-rename submissions.
    const hasAnyCurrentValue = matchingCurrentPaths.some((currentPath) => hasNonEmptyValue(submission[currentPath]))
    if (!hasAnyCurrentValue) {
      return false
    }

    for (const currentPath of matchingCurrentPaths) {
      const currentValue = submission[currentPath]
      if (!hasNonEmptyValue(currentValue)) {
        continue
      }

      if (legacyValue !== currentValue) {
        return false
      }

      const evidence = getAttachmentPathEvidence(submission, legacyKey, currentPath)
      if (evidence === 'both') {
        return false
      }

      if (evidence === 'legacy-only') {
        hasMirroredAttachmentEvidence = true
      }
    }
  }

  return hasMirroredAttachmentEvidence
}

/**
 * Indexes current attachment question paths by leaf question name.
 *
 * Old submissions may still contain stale paths from renamed groups. This map
 * lets dedupe quickly find candidate current paths for a legacy key.
 */
function buildCurrentAttachmentPathsByLeaf(
  asset: AssetResponse,
  flatPaths: Record<string, string>,
): Map<string, string[]> {
  const currentAttachmentPathsByLeaf = new Map<string, string[]>()

  asset.content?.survey?.forEach((row) => {
    if (!ATTACHMENT_QUESTION_TYPES.has(row.type)) {
      return
    }

    const rowName = getRowName(row)
    // `flatPaths` is keyed by name, so namesake questions leave only one path there. Reading
    // `$xpath` keeps them apart - without it, the dedupe below takes one namesake for the
    // other's legacy leftover and drops its column.
    const currentPath = row.$xpath ?? flatPaths[rowName]
    if (!currentPath) {
      return
    }

    const existingPaths = currentAttachmentPathsByLeaf.get(rowName) || []
    if (!existingPaths.includes(currentPath)) {
      currentAttachmentPathsByLeaf.set(rowName, [...existingPaths, currentPath])
    }
  })

  return currentAttachmentPathsByLeaf
}

/**
 * The columns that take over a legacy attachment column's data, or `undefined` when the
 * column has to stay. Returns paths rather than a boolean so the table can tell a cell
 * which other paths its column now stands for.
 */
function findColumnsReplacingLegacyAttachmentColumn(
  key: string,
  allColumns: string[],
  currentAttachmentPathsByLeaf: Map<string, string[]>,
  submissions?: SubmissionResponse[],
): string[] | undefined {
  const keyParts = key.split('/')
  const leafName = keyParts[keyParts.length - 1]
  const currentPaths = currentAttachmentPathsByLeaf.get(leafName)

  if (!currentPaths || currentPaths.includes(key)) {
    return undefined
  }

  const matchingCurrentPaths = currentPaths.filter((currentPath) => allColumns.includes(currentPath))
  if (matchingCurrentPaths.length === 0 || !submissions || submissions.length === 0) {
    return undefined
  }

  if (!shouldDropLegacyAttachmentColumn(submissions, key, matchingCurrentPaths)) {
    return undefined
  }

  return matchingCurrentPaths
}

/**
 * A column followed by the supplemental details columns that belong to it. We
 * move whole blocks around while ordering.
 */
type ColumnsBlock = string[]

function groupColumnsIntoBlocks(columns: string[]): ColumnsBlock[] {
  const blocks: ColumnsBlock[] = []
  columns.forEach((key) => {
    // `injectSupplementalRowsIntoListOfRows` already put every supplemental
    // details column right after its source, so the block being built is the
    // one this column belongs to. The length check is only for the malformed
    // case of a supplemental column with nothing in front of it.
    if (key.startsWith(SUPPLEMENTAL_DETAILS_PROP) && blocks.length > 0) {
      blocks[blocks.length - 1].push(key)
    } else {
      blocks.push([key])
    }
  })
  return blocks
}

/**
 * Moves the block of given key to the beginning of the list (only if the list
 * has such block). Mutates given list.
 */
function moveBlockToFront(blocks: ColumnsBlock[], key: string) {
  const blockIndex = blocks.findIndex((block) => block[0] === key)
  if (blockIndex > -1) {
    blocks.unshift(blocks.splice(blockIndex, 1)[0])
  }
}

/**
 * Whether this key's column holds metadata rather than a response to a form
 * question. The name alone doesn't settle it - nothing reserves the meta question
 * names, so an ordinary question called `today` is a form question like any other.
 */
function isMetadataKey(asset: AssetResponse, key: string): boolean {
  const row = asset.content?.survey?.find((surveyRow) => getRowName(surveyRow) === key)

  // No row leaves nothing to judge by, as the asset only carries its latest
  // survey. Assume metadata, which such a key nearly always is: Back end's own
  // property (e.g. `_id`), or a meta question switched off after some submissions
  // came in. It does mis-file a deleted question named after a meta question.
  return row === undefined || Object.prototype.hasOwnProperty.call(META_QUESTION_TYPES, row.type)
}

/**
 * This is the single source of truth for the order of columns. Takes a list of
 * columns (keys) and returns a new list ordered as:
 *
 * 1. `background-audio` question (if the form has one),
 * 2. `start` and `end` meta questions,
 * 3. all the form questions (in the order they appear in the form definition,
 *    with supplemental details columns following their source question),
 * 4. all the remaining meta questions and additional submission properties
 *    (in `LAST_COLUMNS_ORDER` order, `meta/rootUuid` being the very last one).
 *
 * Columns outside of `LAST_COLUMNS_ORDER` keep the order they were given in,
 * which is the form definition order, as that's how `getAllDataColumns` builds
 * the list.
 *
 * Every place that shows users a list of columns has to use this, so that they
 * agree with each other.
 */
export function orderColumns(asset: AssetResponse, columns: string[]): string[] {
  const allBlocks = groupColumnsIntoBlocks(columns)

  // Split off the tail columns, then sort them by their position in
  // `LAST_COLUMNS_ORDER` - whatever order they arrived in doesn't matter, which
  // is what lets us feed this function columns discovered from submission data.
  const isTailBlock = (block: ColumnsBlock) => LAST_COLUMNS_ORDER.includes(block[0]) && isMetadataKey(asset, block[0])
  const lastBlocks = allBlocks.filter(isTailBlock)
  const blocks = allBlocks.filter((block) => !isTailBlock(block))
  lastBlocks.sort((blockA, blockB) => LAST_COLUMNS_ORDER.indexOf(blockA[0]) - LAST_COLUMNS_ORDER.indexOf(blockB[0]))

  // Each of these moves to the front, so the order of the calls is reversed:
  // `background-audio` goes last to end up first, then `start`, then `end`.
  // `start` and `end` only move when they really are the meta questions.
  if (isMetadataKey(asset, META_QUESTION_TYPES.end)) {
    moveBlockToFront(blocks, META_QUESTION_TYPES.end)
  }
  if (isMetadataKey(asset, META_QUESTION_TYPES.start)) {
    moveBlockToFront(blocks, META_QUESTION_TYPES.start)
  }
  const backgroundAudioName = getBackgroundAudioQuestionName(asset)
  if (backgroundAudioName !== null) {
    moveBlockToFront(blocks, backgroundAudioName)
  }

  return [...blocks, ...lastBlocks].flat()
}

export interface DataColumns {
  /** The column keys, in the order Data Table shows them (see `orderColumns`). */
  columns: string[]
  /**
   * The legacy xpaths each column absorbed, keyed by the column that survived the dedupe
   * below. A question moved between groups gets one column, but a pre-move submission keeps
   * its file under the old path, so a cell has to look under both. Empty for most columns.
   */
  legacyAttachmentPathsByColumn: Map<string, string[]>
}

/**
 * Returns a complete and unique list of columns (keys) that contain displayable
 * data that is useful for users, plus the legacy attachment paths the dedupe
 * collapsed into them (see `DataColumns`).
 *
 * Gathers all possible columns based on asset survey definition and optionally
 * all the columns from provided submissions. Passing submissions is useful for
 * a case when previous asset version had more/different rows than current one.
 *
 * NOTE: includes supplemental details columns (AKA processing columns).
 */
export function getAllDataColumnsWithAliases(
  asset: AssetResponse,
  submissions?: SubmissionResponse[],
  bulkActions?: BulkActionResponse[],
): DataColumns {
  if (asset.content?.survey === undefined) {
    throw new Error('Asset has no content')
  }

  const flatPaths = getSurveyFlatPaths(asset.content.survey, false, true)

  // add all questions from the survey definition
  let output = recordValues(flatPaths)

  if (submissions) {
    // Gather unique columns from all provided submissions and add them to output
    const dataKeys = recordKeys(submissions.reduce((result, obj) => Object.assign(result, obj), {}))
    output = [...new Set([...output, ...dataKeys])]
  }

  // exclude some technical non-data columns
  output = output.filter((key) => EXCLUDED_COLUMNS.includes(key) === false)

  // Deduplicate stale attachment keys from old submissions when current schema
  // has the same question under a different (usually renamed-group) path.
  // Keep this conservative by collapsing only when old and current keys share
  // a non-empty overlapping value in at least one submission.
  const currentAttachmentPathsByLeaf = buildCurrentAttachmentPathsByLeaf(asset, flatPaths)

  const legacyAttachmentPathsByColumn = new Map<string, string[]>()
  output = output.filter((key) => {
    const replacingColumns = findColumnsReplacingLegacyAttachmentColumn(
      key,
      output,
      currentAttachmentPathsByLeaf,
      submissions,
    )
    if (replacingColumns === undefined) {
      return true
    }

    // This column is about to disappear, and the columns taking over have to find its files.
    for (const replacingColumn of replacingColumns) {
      legacyAttachmentPathsByColumn.set(replacingColumn, [
        ...(legacyAttachmentPathsByColumn.get(replacingColumn) ?? []),
        key,
      ])
    }
    return false
  })

  // Drop the rows whose type never carries a response, by type rather than name,
  // so an ordinary question named `audit` survives (see `EXCLUDED_ROW_TYPES`).
  output = output.filter((key) => {
    const foundPathKey = recordKeys(flatPaths).find((pathKey) => flatPaths[pathKey] === key)

    // No path means this key is not a survey row, so it has no type to judge
    if (!foundPathKey) {
      return true
    }

    const foundRow = asset?.content?.survey?.find((row) => foundPathKey === getRowName(row))

    return foundRow === undefined || !EXCLUDED_ROW_TYPES.includes(foundRow.type)
  })

  // exclude kobomatrix rows as data is not directly tied to them, but
  // to rows user answered to, thus making these columns always empty
  const excludedMatrixKeys: string[] = []
  let isInsideKoboMatrix = false
  asset.content.survey.forEach((row) => {
    if (row.type === GROUP_TYPES_BEGIN.begin_kobomatrix) {
      isInsideKoboMatrix = true
    } else if (row.type === GROUP_TYPES_END.end_kobomatrix) {
      isInsideKoboMatrix = false
    } else if (isInsideKoboMatrix) {
      const rowPath = flatPaths[getRowName(row)]
      excludedMatrixKeys.push(rowPath)
    }
  })
  output = output.filter((key) => excludedMatrixKeys.includes(key) === false)

  // Exclude repeat groups and regular groups as all of their data is handled
  // by children rows.
  // This also fixes the issue when a repeat group in older version becomes
  // a regular group in new form version (with the same name), and the Table
  // was displaying "[object Object]" as responses.
  const excludedGroups: string[] = []
  const flatPathsWithGroups = getSurveyFlatPaths(asset.content.survey, true)
  asset.content.survey.forEach((row) => {
    if (row.type === GROUP_TYPES_BEGIN.begin_repeat || row.type === GROUP_TYPES_BEGIN.begin_group) {
      const rowPath = flatPathsWithGroups[getRowName(row)]
      excludedGroups.push(rowPath)
    }
  })
  output = output.filter((key) => excludedGroups.includes(key) === false)

  const virtualSupplementalFields = getVirtualSupplementalFieldsForBulkActions(bulkActions)
  output = injectSupplementalRowsIntoListOfRows(asset, output, virtualSupplementalFields)

  // Ordering happens here, at the very end, rather than in each consumer - that
  // way everything built on top of this function agrees on the order for free
  // (see `orderColumns`).
  return { columns: orderColumns(asset, output), legacyAttachmentPathsByColumn }
}

/** The column keys alone, for callers that list columns and render no cells. */
export function getAllDataColumns(
  asset: AssetResponse,
  submissions?: SubmissionResponse[],
  bulkActions?: BulkActionResponse[],
): string[] {
  return getAllDataColumnsWithAliases(asset, submissions, bulkActions).columns
}

/**
 * Returns a list of the metadata columns (keys) - i.e. the columns that are not
 * responses to the form questions:
 *
 * 1. meta questions (the Form Builder checkboxes, e.g. `start`, `today`) - only
 *    the ones that the form actually defines, and that carry a response worth
 *    showing (`audit` doesn't, see `EXCLUDED_ROW_TYPES`),
 * 2. additional submission properties added by Back end (e.g. `_id`).
 *
 * We filter `getAllDataColumns` down instead of building a list of our own, so
 * that these columns come in the same order as in Data Table and can never
 * include something Data Table wouldn't show (see `orderColumns`).
 *
 * @param submissions - pass these to get the properties of the form version
 * a submission was made with, not just the ones the current version defines
 */
export function getMetadataColumns(asset: AssetResponse, submissions?: SubmissionResponse[]) {
  const metaRowNames = new Set(
    (asset.content?.survey || [])
      .filter((row) => Object.prototype.hasOwnProperty.call(META_QUESTION_TYPES, row.type))
      .map((row) => getRowName(row)),
  )

  // Two sources, because the two kinds of metadata are found in different
  // places: meta questions come from the form definition, while the additional
  // submission properties only ever show up in submission data.
  return getAllDataColumns(asset, submissions).filter(
    (key) => metaRowNames.has(key) || (LAST_COLUMNS_ORDER.includes(key) && isMetadataKey(asset, key)),
  )
}

export interface TableFilterQuery {
  queryString: string
  queryObj: {
    [key: string]: string | null | { $in: number[] } | { $regex: string; $options: string }
  }
}

/**
 * This function uses filters list from `react-table` output to produce queries
 * that our Back end can understand. We use it it multiple places that intensely
 * need to use identical output. We might simply return `queryObj` and make
 * the code stringify it by itself, but it will make it less robust.
 */
export function buildFilterQuery(
  /** Whole survey of given asset - we need it to get questions types */
  survey: SurveyRow[],
  /** List of `react-table` filters */
  filters: Filter[],
): TableFilterQuery {
  const output: TableFilterQuery = {
    queryString: '',
    queryObj: {},
  }

  filters.forEach((filter) => {
    switch (filter.id) {
      case '_id': {
        output.queryObj[filter.id] = { $in: [Number.parseInt(filter.value)] }
        break
      }
      case VALIDATION_STATUS_ID_PROP: {
        if (filter.value === ValidationStatusAdditionalName.no_status) {
          output.queryObj[filter.id] = null
        } else {
          output.queryObj[filter.id] = filter.value
        }
        break
      }
      // Apart from few exceptions in above cases, we tend to treat all columns
      // (`filter.id`s) with the same algorithm
      default: {
        // We assume `filter.id` is the question name (Data Table column name)
        const foundRow = survey.find((row) => getRowName(row) === filter.id)

        // Some question types needs the data to be filtered by exact values
        // (e.g. "yes" shouldn't mach "yessica" or "yes and no")
        if (foundRow && FILTER_EXACT_TYPES.includes(foundRow.type)) {
          output.queryObj[filter.id] = {
            $regex: `^${filter.value}$`,
            $options: 'i',
          }
        } else {
          output.queryObj[filter.id] = { $regex: filter.value, $options: 'i' }
        }
        break
      }
    }
  })

  if (recordKeys(output.queryObj).length > 0) {
    output.queryString = JSON.stringify(output.queryObj)
  }

  return output
}

export interface SelectResponseLabelOptions {
  /** Raw response, as stored in submission data. */
  value: string
  questionType: AnyRowTypeName | undefined
  /** The question's `select_from_list_name`. */
  listName: string | undefined
  /** All of the form's choices, not just the ones of this question's list. */
  choices: SurveyChoice[]
  /**
   * Index into a choice `label` array. A negative one (the "Question & choice
   * names" setting) matches no label, so every value comes back raw — but
   * `select_multiple` values still get re-joined with commas, so callers that
   * need the response exactly as stored don't come here.
   */
  translationIndex: number
}

/**
 * Builds the human-readable value of a `select_one` or `select_multiple`
 * response, i.e. the label(s) of the selected choice(s).
 *
 * Choices can be renamed in the Form Builder, while old submissions keep the
 * `name` that was current when they came in. Any value that no longer matches a
 * choice is shown raw, because dropping it would imply it was never selected.
 */
export function getSelectResponseLabel(options: SelectResponseLabelOptions): string {
  const { value, questionType, listName, choices, translationIndex } = options

  // `select_multiple` is the only type that packs several values into one
  // string. Splitting any other type would mangle values that contain spaces.
  let values = [value]
  if (questionType === QUESTION_TYPES.select_multiple.id) {
    // Filtering empties keeps stray spaces from becoming dangling separators.
    values = value.split(' ').filter((valueItem) => valueItem !== '')
  }

  return values
    .map((valueItem) => {
      // Choice names are only unique within their own list, hence matching both.
      const choice = choices.find((choiceItem) => choiceItem.list_name === listName && choiceItem.name === valueItem)
      // A choice may exist but have no label in this translation, so this
      // fallback covers more than just renamed choices.
      return choice?.label?.[translationIndex] || valueItem
    })
    .join(', ')
}

/**
 * For checking if given column from Data Table should display a text input
 * filter. It works for columns associated with form questions and for other
 * columns too (e.g. submission metadata).
 */
export function isTableColumnFilterableByTextInput(questionType: AnyRowTypeName | undefined, columnId: string) {
  return (
    (questionType && TEXT_FILTER_QUESTION_TYPES.includes(questionType)) || TEXT_FILTER_QUESTION_IDS.includes(columnId)
  )
}

/**
 * For checking if given column from Data Table should display a dropdown
 * filter.
 */
export function isTableColumnFilterableByDropdown(questionType: AnyRowTypeName | undefined) {
  return questionType && DROPDOWN_FILTER_QUESTION_TYPES.includes(questionType)
}

/**
 * Returns xpaths of the audio question columns among the given Data Table
 * columns. Pass the columns the table is rendering to get only the audio
 * columns the user can actually see, which is how we avoid looking up durations
 * for hidden columns.
 */
export function getVisibleAudioXpaths(columns: TableColumn[]): string[] {
  const xpaths: string[] = []
  columns.forEach((column) => {
    const question = column.question
    const xpath = question?.$xpath
    if (
      xpath &&
      (question?.type === QUESTION_TYPES.audio.id || question?.type === QUESTION_TYPES['background-audio'].id)
    ) {
      xpaths.push(xpath)
    }
  })
  return xpaths
}

/**
 * How much of a response a one-line cell keeps. More than a column dragged across a 4K screen can fit (French prose
 * measures about 6px a character at the Table's 13px), and well under the 10240 characters at which Safari gives up.
 */
export const CELL_PREVIEW_MAX_LENGTH = 1000

/**
 * Shortens a response to what a one-line, ellipsis-trimmed Data Table cell can show.
 * Values that are not text pass through untouched.
 */
export function getCellPreviewText<T>(value: T): T {
  if (typeof value !== 'string' || value.length <= CELL_PREVIEW_MAX_LENGTH) {
    return value
  }

  const preview = value.slice(0, CELL_PREVIEW_MAX_LENGTH)
  // Cutting a surrogate pair (how JavaScript stores a character that doesn't fit in 16 bits, e.g. an emoji) in half
  // would leave a `�` as the last character.
  return (/[\uD800-\uDBFF]$/.test(preview) ? preview.slice(0, -1) : preview) as T
}
